import { Link, NavLink, useLocation } from "react-router-dom";
import { LockKey, House, Megaphone, ClipboardText, Sparkle, Wrench, Info } from "@phosphor-icons/react";
import PhoneText from "@/components/PhoneText";
import Logo from "@/components/Logo";
import { useBuilding } from "@/context/BuildingContext";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/signaler", label: "Signaler", Icon: Megaphone },
  { to: "/signalements", label: "Signalements", Icon: ClipboardText },
  { to: "/entretien", label: "Entretien", Icon: Sparkle, feature: "cleaning" },
  { to: "/services", label: "Services", Icon: Wrench },
  { to: "/infos", label: "Infos", Icon: Info },
];

const useNav = () => {
  const building = useBuilding();
  return NAV.filter((n) => !n.feature || building.features?.[n.feature] !== false);
};

export function Header() {
  const building = useBuilding();
  const nav = useNav();
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const showAnnouncement =
    building.announcement_text &&
    building.announcement_enabled !== false &&
    (!building.announcement_from || building.announcement_from <= today) &&
    (!building.announcement_until || building.announcement_until >= today);
  return (
    <header className="sticky top-0 z-40 border-b border-black/5 bg-[#F7F5F0]/85 backdrop-blur-xl">
      {showAnnouncement && (
        <div className="bg-primary text-primary-foreground" data-testid="announcement-banner">
          <div className="mx-auto flex max-w-5xl items-center justify-center gap-2 px-5 py-2">
            <Megaphone size={14} weight="fill" className="shrink-0" />
            <p className="text-center text-xs font-semibold leading-snug">
              <PhoneText text={building.announcement_text} linkClassName="font-bold underline underline-offset-2" />
            </p>
          </div>
        </div>
      )}
      <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5">
        <Link to="/" className="flex min-w-0 items-center gap-3" data-testid="header-logo-link">
          <Logo size={36} />
          <span className="leading-tight">
            <span className="block truncate font-display text-base font-bold tracking-tight">Ma Copropriété</span>
            <span className="-mt-0.5 block truncate text-[11px] font-medium text-muted-foreground">{building.name}</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-1 md:flex" data-testid="desktop-nav">
          {nav.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200",
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-black/5 hover:text-foreground"
                )
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <Link
          to="/admin"
          data-testid="admin-link"
          aria-label="Espace gestionnaire"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-black/5 bg-white text-foreground/60 transition-colors duration-200 hover:bg-primary hover:text-primary-foreground"
        >
          <LockKey size={18} weight="duotone" />
        </Link>
      </div>
    </header>
  );
}

export function MobileNav() {
  const location = useLocation();
  const nav = useNav();
  const items = [{ to: "/", label: "Accueil", Icon: House }, ...nav];
  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 border-t border-black/5 bg-white/90 backdrop-blur-xl md:hidden"
      data-testid="mobile-nav"
    >
      <div className={cn("grid px-1 pb-[env(safe-area-inset-bottom)]", items.length > 5 ? "grid-cols-6" : "grid-cols-5")}>
        {items.map(({ to, label, Icon }) => {
          const active = location.pathname === to;
          return (
            <Link
              key={to}
              to={to}
              data-testid={`mobile-nav-${label.toLowerCase()}`}
              className={cn(
                "flex flex-col items-center gap-1 py-2.5 text-[9px] font-semibold tracking-tight transition-colors duration-200",
                active ? "text-primary" : "text-foreground/50"
              )}
            >
              <Icon size={21} weight={active ? "fill" : "duotone"} />
              {label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
